import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { useStageTokens } from '../hooks/useStageTokens';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';

interface SpellingActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/** Shuffle letters ensuring the tiles do not accidentally spell the target word */
function shuffleTiles(word: string): string[] {
    const arr = word.split('');
    if (arr.length <= 1) return arr;
    const uniqueChars = new Set(arr);
    if (uniqueChars.size <= 1) return arr;

    const copy = [...arr];
    let attempts = 0;
    while (attempts < 10 && copy.join('') === word) {
        for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
        }
        attempts++;
    }

    if (copy.join('') === word && copy.length >= 2) {
        for (let i = 0; i < copy.length - 1; i++) {
            if (copy[i] !== copy[i + 1]) {
                [copy[i], copy[i + 1]] = [copy[i + 1], copy[i]];
                break;
            }
        }
    }
    return copy;
}

export const SpellingActivity: React.FC<SpellingActivityProps> = ({ activity, onComplete, keyStage }) => {
    const word: string = (activity.config?.word || '').trim().toLowerCase();
    const hintText: string | undefined = activity.config?.hint;
    const tokens = useStageTokens(keyStage);

    // Shuffled tiles — stable across renders using useMemo and guaranteed deranged
    const tiles = useMemo(() => shuffleTiles(word), [word]);

    // Tracking tapped letters and the specific tile index for each tapped slot
    const [tapped, setTapped] = useState<string[]>([]);
    const [usedTileIndices, setUsedTileIndices] = useState<number[]>([]);
    const [hasSubmittedWrong, setHasSubmittedWrong] = useState<boolean>(false);

    // Reset state whenever target word / activity changes
    useEffect(() => {
        setTapped([]);
        setUsedTileIndices([]);
        setHasSubmittedWrong(false);
    }, [word, activity.id]);

    const handleTile = useCallback((letter: string, tileIdx: number) => {
        if (usedTileIndices.includes(tileIdx)) return;
        if (tapped.length >= word.length) return;

        setTapped(prev => [...prev, letter]);
        setUsedTileIndices(prev => [...prev, tileIdx]);
        setHasSubmittedWrong(false);
    }, [usedTileIndices, tapped.length, word.length]);

    const handleBackspace = useCallback(() => {
        if (tapped.length === 0) return;
        setTapped(prev => prev.slice(0, -1));
        setUsedTileIndices(prev => prev.slice(0, -1));
        setHasSubmittedWrong(false);
    }, [tapped.length]);

    // Tap-to-remove a specific filled blank
    const handleBlankPress = useCallback((index: number) => {
        if (index >= tapped.length) return;
        setTapped(prev => prev.filter((_, i) => i !== index));
        setUsedTileIndices(prev => prev.filter((_, i) => i !== index));
        setHasSubmittedWrong(false);
    }, [tapped.length]);

    const handleHint = useCallback(() => {
        if (word.length === 0) return;
        setHasSubmittedWrong(false);

        // Find how many letters from the beginning are currently correct
        let matchCount = 0;
        while (matchCount < tapped.length && matchCount < word.length && tapped[matchCount] === word[matchCount]) {
            matchCount++;
        }

        let newTapped = tapped.slice(0, matchCount);
        let newUsed = usedTileIndices.slice(0, matchCount);

        if (matchCount >= word.length) return;

        const nextLetter = word[matchCount];

        // Find the first unused tile matching this letter
        const availableTileIndex = tiles.findIndex(
            (tileLetter, idx) => tileLetter.toLowerCase() === nextLetter && !newUsed.includes(idx)
        );

        if (availableTileIndex !== -1) {
            newTapped = [...newTapped, nextLetter];
            newUsed = [...newUsed, availableTileIndex];
            setTapped(newTapped);
            setUsedTileIndices(newUsed);
        }
    }, [word, tapped, usedTileIndices, tiles]);

    const currentAnswer = tapped.join('');
    const isCorrect = currentAnswer === word;
    const isComplete = tapped.length === word.length && word.length > 0;

    const handleSubmit = useCallback(() => {
        if (!isComplete) return;
        if (!isCorrect) {
            setHasSubmittedWrong(true);
            return;
        }
        useStrokeStore.getState().setStructuredData({ tapped: currentAnswer, expected: word, correct: true });
        onComplete();
    }, [isComplete, isCorrect, currentAnswer, word, onComplete]);

    // Auto-advance after brief celebration when correct
    useEffect(() => {
        if (isCorrect && isComplete) {
            const timer = setTimeout(() => {
                handleSubmit();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isCorrect, isComplete, handleSubmit]);

    // Physical keyboard listener for web / PWA / Chromebooks
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;

            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                handleBackspace();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (isComplete) {
                    handleSubmit();
                }
            } else if (/^[a-zA-Z]$/.test(e.key)) {
                const pressed = e.key.toLowerCase();
                const tileIndex = tiles.findIndex(
                    (tileLetter, idx) => tileLetter.toLowerCase() === pressed && !usedTileIndices.includes(idx)
                );
                if (tileIndex !== -1 && tapped.length < word.length) {
                    e.preventDefault();
                    handleTile(pressed, tileIndex);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
                window.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [tiles, usedTileIndices, tapped.length, word.length, isComplete, handleBackspace, handleSubmit, handleTile]);

    const minBtnHeight = tokens.touchTarget || 52;

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
            bounces={false}
        >
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerRow}>
                    <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {getStudentSafeActivityTitle(activity)}
                    </Text>
                </View>
                {activity.instructions && (
                    <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {activity.instructions}
                    </Text>
                )}
                {hintText && (
                    <Text style={styles.textHint} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        💡 Hint: {hintText}
                    </Text>
                )}
            </View>

            {/* Answer slots — underscore blanks, tap-to-remove enabled on filled blanks */}
            <View testID="spelling-blanks" style={styles.blanksRow}>
                {word.split('').map((_, i) => {
                    const isFilled = Boolean(tapped[i]);
                    return (
                        <TouchableOpacity
                            key={i}
                            style={[styles.blank, isFilled ? styles.blankFilled : {}]}
                            onPress={() => handleBlankPress(i)}
                            disabled={!isFilled}
                            activeOpacity={0.7}
                            accessibilityRole={isFilled ? 'button' : 'text'}
                            accessibilityLabel={isFilled ? `Letter ${tapped[i]}, tap to delete` : `Blank slot ${i + 1}`}
                            accessibilityHint={isFilled ? 'Double tap to remove this letter' : undefined}
                        >
                            <Text style={styles.blankText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {tapped[i] || ''}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {/* Hidden answer display for tests */}
            <Text testID="spelling-answer-display" style={styles.hiddenDisplay} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                {currentAnswer}
            </Text>

            {/* Feedback pill — never leaks the solution word */}
            {isComplete && isCorrect && (
                <View style={[styles.feedback, styles.feedbackCorrect]} accessibilityLiveRegion="polite">
                    <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        ✓ Correct! Well done! 🎉
                    </Text>
                </View>
            )}
            {hasSubmittedWrong && (
                <View style={[styles.feedback, styles.feedbackWrong]} accessibilityLiveRegion="polite">
                    <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        ✗ Not quite — try again!
                    </Text>
                </View>
            )}

            {/* Letter tiles — greyed out and disabled once used */}
            <View style={styles.tilesRow}>
                {tiles.map((letter, i) => {
                    const isUsed = usedTileIndices.includes(i);
                    return (
                        <TouchableOpacity
                            key={i}
                            style={[styles.tile, isUsed && styles.tileUsed]}
                            onPress={() => handleTile(letter, i)}
                            disabled={isUsed}
                            activeOpacity={0.7}
                            accessibilityRole="button"
                            accessibilityLabel={isUsed ? `Letter ${letter}, used` : `Letter ${letter}`}
                            accessibilityState={{ disabled: isUsed }}
                        >
                            <Text style={[styles.tileText, isUsed && styles.tileTextUsed]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {letter}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {/* Controls: Hint, Delete, Done */}
            <View style={styles.controls}>
                <TouchableOpacity
                    style={[styles.hintBtn, { minHeight: minBtnHeight }, isComplete && isCorrect && styles.disabledBtn]}
                    onPress={handleHint}
                    disabled={isComplete && isCorrect}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Hint"
                    accessibilityHint="Gives you the next letter in the word"
                >
                    <Text style={styles.hintTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>💡 Hint</Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.backspaceBtn, { minHeight: minBtnHeight }, tapped.length === 0 && styles.disabledBtn]}
                    onPress={handleBackspace}
                    disabled={tapped.length === 0}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Delete"
                    accessibilityHint="Removes the last letter you tapped"
                >
                    <Text style={styles.backspaceTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>⌫ Delete</Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.submitBtn, { minHeight: minBtnHeight }, !isComplete && styles.disabledBtn]}
                    onPress={handleSubmit}
                    disabled={!isComplete}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your spelling answer"
                    accessibilityState={{ disabled: !isComplete }}
                >
                    <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, width: '100%', minHeight: 0, backgroundColor: '#fff' },
    scrollContent: { flexGrow: 1, padding: 16, justifyContent: 'space-around' },
    header: { marginBottom: 12 },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 },
    title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
    instructions: { fontSize: 14, color: '#64748B', fontStyle: 'italic' },
    textHint: { fontSize: 14, color: '#D97706', fontWeight: '600', marginTop: 4, backgroundColor: '#FEF3C7', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, alignSelf: 'flex-start' },
    blanksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginVertical: 8 },
    blank: {
        width: 44, height: 52, borderBottomWidth: 3, borderBottomColor: '#94A3B8',
        alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 2,
    },
    blankFilled: { borderBottomColor: '#6366F1' },
    blankText: { fontSize: 26, fontWeight: 'bold', color: '#1E293B' },
    hiddenDisplay: { height: 0, overflow: 'hidden', opacity: 0 },
    feedback: { alignSelf: 'center', paddingHorizontal: 16, paddingVertical: 6, borderRadius: 16, marginVertical: 8 },
    feedbackCorrect: { backgroundColor: '#D1FAE5' },
    feedbackWrong: { backgroundColor: '#FEE2E2' },
    feedbackText: { fontWeight: 'bold', fontSize: 15, color: '#1E293B' },
    tilesRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginVertical: 12 },
    tile: {
        width: 60, height: 60, backgroundColor: '#EEF2FF', borderRadius: 14,
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 2, borderColor: '#C7D2FE',
        shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06, shadowRadius: 2, elevation: 1,
    },
    tileUsed: {
        backgroundColor: '#E2E8F0',
        borderColor: '#CBD5E1',
        opacity: 0.45,
        elevation: 0,
        shadowOpacity: 0,
    },
    tileText: { fontSize: 28, fontWeight: 'bold', color: '#4F46E5' },
    tileTextUsed: { color: '#94A3B8' },
    controls: { flexDirection: 'row', gap: 10, marginTop: 8 },
    hintBtn: {
        flex: 1, height: 56, backgroundColor: '#FFFBEB', borderRadius: 14,
        alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: '#FDE68A',
    },
    hintTxt: { fontSize: 16, fontWeight: 'bold', color: '#D97706' },
    backspaceBtn: {
        flex: 1, height: 56, backgroundColor: '#FEF2F2', borderRadius: 14,
        alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#FECACA',
    },
    backspaceTxt: { fontSize: 16, fontWeight: 'bold', color: '#EF4444' },
    submitBtn: {
        flex: 1.5,
        height: 56,
        backgroundColor: '#22c55e',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: { backgroundColor: '#E2E8F0', borderColor: '#CBD5E1', opacity: 0.6, shadowOpacity: 0, elevation: 0 },
    submitTxt: { fontSize: 18, fontWeight: '900', color: '#fff', letterSpacing: 0.5 },
});
